/**
 * Security medallion (the round "SAFE" disc by the signature line)
 * colour + finish design.
 *
 * The owner picked the disc on his reference check — an ash-grey
 * medallion — and asked for colour selection plus finish styles,
 * including a shiny metallic look. Two finishes:
 *
 * - 'flat': the classic translucent ink disc (the gradient recipe
 *   the studio has always used, now driven by the chosen colour
 *   instead of the design's secondary colour).
 * - 'metallic': an opaque bright gradient — white-hot centre,
 *   light shoulder, saturated base, deep edge — that renders the
 *   foil-style ARTWORK. Honest limit, stated in the panel too: the
 *   file carries the bright metallic design and is foil-ready, but
 *   a normal printer cannot lay down real reflective foil; true
 *   shine on paper comes from foil/specialty printing.
 */

export type SealFinish = 'flat' | 'metallic';

export interface SealColorPreset {
    id: string;
    name: string;
    color: string;
}

/** Ash grey first: it is the default, matching the owner's reference. */
export const SEAL_COLOR_PRESETS: SealColorPreset[] = [
    { id: 'ash', name: 'Ash Grey', color: '#a7adb5' },
    { id: 'blue', name: 'Blue', color: '#3b6ea5' },
    { id: 'green', name: 'Green', color: '#3f7d5a' },
    { id: 'gold', name: 'Gold', color: '#b8912f' },
    { id: 'burgundy', name: 'Burgundy', color: '#7a2436' },
    { id: 'navy', name: 'Navy', color: '#1f3350' }
];

export const DEFAULT_SEAL_COLOR = '#a7adb5';
export const DEFAULT_SEAL_FINISH: SealFinish = 'flat';

/** Shade a #rrggbb colour toward white (amt > 0) or black (amt < 0). */
export function shadeHex(hex: string, amt: number): string {
    const m = /^#?([0-9a-fA-F]{6})$/.exec(String(hex || '').trim());
    if (!m) return '#a7adb5';
    const n = parseInt(m[1], 16);
    const t = Math.max(-1, Math.min(1, amt));
    const chan = (c: number) => Math.round(t >= 0 ? c + (255 - c) * t : c * (1 + t));
    const r = chan((n >> 16) & 255), g = chan((n >> 8) & 255), b = chan(n & 255);
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}

export interface SealGradientStop {
    offset: number;
    color: string;
}

/** Gradient stops for the medallion body, per finish. */
export function sealGradientStops(color: string, finish: SealFinish): SealGradientStop[] {
    const base = /^#?[0-9a-fA-F]{6}$/.test(String(color || '').trim()) ? color : DEFAULT_SEAL_COLOR;
    if (finish === 'metallic') {
        return [
            { offset: 0, color: '#ffffff' },
            { offset: 0.28, color: shadeHex(base, 0.45) },
            { offset: 0.62, color: base },
            { offset: 1, color: shadeHex(base, -0.38) }
        ];
    }
    // Flat: the studio's long-standing translucent ink recipe.
    return [
        { offset: 0, color: `${base}80` },
        { offset: 0.7, color: `${base}A0` },
        { offset: 1, color: `${base}C0` }
    ];
}

/** Rim stroke for the medallion body, per finish. */
export function sealStroke(color: string, finish: SealFinish): string {
    const base = /^#?[0-9a-fA-F]{6}$/.test(String(color || '').trim()) ? color : DEFAULT_SEAL_COLOR;
    return finish === 'metallic' ? shadeHex(base, -0.45) : `${base}60`;
}
